import { motion, useReducedMotion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { DUR, EASE_IN_OUT_CUBIC, EASE_OUT_QUINT } from '@/lib/motion';

/**
 * Surfaces — four ways to run it.
 *
 * Not a bento grid, not an icon-and-blurb card pattern. Each surface
 * gets a small, characterful inline mock of the actual front end:
 * a terminal with a real command, a Windows-style window with a
 * progress strip, a phone, a browser tab. Hover the surface — its
 * mock plays its own micro-interaction.
 *
 * The headline below each mock is the surface name; the supporting
 * line states the platform stack. No CTAs jammed onto the cards.
 */

type Surface = 'cli' | 'desktop' | 'mobile' | 'web';

const SURFACES: Array<{
  id: Surface;
  name: string;
  stack: string;
  status: 'shipping' | 'soon';
  href: string;
  /** Atmosphere — each card gets its own lighting origin so the four
   *  feel like four product shots, not four identical tiles. */
  lightOrigin: string;
}> = [
  {
    id: 'cli',
    name: 'Command line',
    stack: 'C++17 · CLI11 · Cross-platform',
    status: 'shipping',
    href: 'https://github.com/MarkoSlobodjan/slobozip',
    lightOrigin: 'top right',
  },
  {
    id: 'desktop',
    name: 'Windows desktop',
    stack: 'WPF · .NET 8',
    status: 'shipping',
    href: 'https://github.com/MarkoSlobodjan/slobozip/releases',
    lightOrigin: 'top left',
  },
  {
    id: 'web',
    name: 'In your browser',
    stack: 'WebAssembly · Emscripten',
    status: 'shipping',
    href: '#squeeze',
    lightOrigin: 'bottom left',
  },
  {
    id: 'mobile',
    name: 'Phone, eventually',
    stack: '.NET MAUI · Android · iOS',
    status: 'soon',
    href: '#',
    lightOrigin: 'bottom right',
  },
];

export function Surfaces() {
  return (
    <section id="surfaces" style={{ padding: '160px 0', position: 'relative' }}>
      <div className="container-x">
        <span
          className="t-mono"
          style={{
            fontSize: 'var(--type-xs)',
            color: 'var(--accent)',
            letterSpacing: 'var(--tracking-mono-caps)',
            textTransform: 'uppercase',
          }}
        >
          surfaces
        </span>
        <h2
          className="t-heading"
          style={{
            marginTop: 16,
            fontSize: 'var(--type-xl)',
            maxWidth: '20ch',
          }}
        >
          Four ways to run it.
        </h2>
        <p
          style={{
            marginTop: 20,
            fontSize: 'var(--type-md)',
            color: 'var(--text-2)',
            maxWidth: '60ch',
            lineHeight: 1.55,
          }}
        >
          Same C++17 core compiled four ways. Each surface is below; hover one
          to watch it move.
        </p>

        <div
          style={{
            marginTop: 64,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 28,
          }}
        >
          {SURFACES.map((s) => (
            <SurfaceCard key={s.id} surface={s} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Card ──────────────────────────────────────────────────────────── */

function SurfaceCard({
  surface,
}: {
  surface: (typeof SURFACES)[number];
}) {
  const [hover, setHover] = useState(false);
  const reduced = useReducedMotion() ?? false;
  const coarse = useIsCoarsePointer();
  // On touch, hover never fires — leaving rim/mocks stuck in idle. Treat
  // the card as permanently "active" so the user sees the micro-
  // interaction without needing a pointer.
  const active = coarse ? !reduced : hover && !reduced;

  return (
    <a
      href={surface.href}
      target={surface.href.startsWith('http') ? '_blank' : undefined}
      rel={surface.href.startsWith('http') ? 'noreferrer' : undefined}
      onMouseEnter={() => !coarse && setHover(true)}
      onMouseLeave={() => !coarse && setHover(false)}
      onFocus={() => !coarse && setHover(true)}
      onBlur={() => !coarse && setHover(false)}
      style={{
        display: 'block',
        position: 'relative',
        padding: 24,
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 16,
        transition:
          'border-color var(--dur-base) var(--ease-out-expo), transform var(--dur-base) var(--ease-out-expo), box-shadow var(--dur-base) var(--ease-out-expo)',
        transform: !coarse && active ? 'translateY(-3px)' : 'translateY(0)',
        borderColor: active ? 'var(--accent-soft)' : 'var(--border)',
        boxShadow: !coarse && active ? '0 18px 50px rgba(0,0,0,0.06)' : '0 1px 0 var(--border)',
      }}
    >
      {/* Atmosphere — directional rim light, unique per surface. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(ellipse at ${surface.lightOrigin}, var(--accent-glow) 0%, transparent 55%)`,
          opacity: active ? 0.7 : 0.18,
          transition: 'opacity var(--dur-base) var(--ease-out-expo)',
          pointerEvents: 'none',
          borderRadius: 16,
        }}
      />

      {/* Mock area */}
      <div
        aria-hidden
        style={{
          position: 'relative',
          height: 168,
          marginInline: -24,
          marginTop: -24,
          padding: 18,
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg-warm)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {surface.id === 'cli' && <TerminalMock active={active} />}
        {surface.id === 'desktop' && <WindowMock active={active} />}
        {surface.id === 'web' && <BrowserMock active={active} />}
        {surface.id === 'mobile' && <PhoneMock active={active} />}
      </div>

      {/* Caption */}
      <div style={{ paddingTop: 22, position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
          <h3
            style={{
              fontSize: 'var(--type-md)',
              fontWeight: 600,
              letterSpacing: 'var(--tracking-tight)',
              color: 'var(--text)',
            }}
          >
            {surface.name}
          </h3>
          <StatusPill status={surface.status} />
        </div>
        <p
          className="t-mono"
          style={{
            marginTop: 8,
            fontSize: 'var(--type-xs)',
            color: 'var(--text-3)',
            letterSpacing: 'var(--tracking-mono-caps)',
            textTransform: 'uppercase',
          }}
        >
          {surface.stack}
        </p>
      </div>
    </a>
  );
}

function StatusPill({ status }: { status: 'shipping' | 'soon' }) {
  const isSoon = status === 'soon';
  return (
    <span
      className="t-mono"
      style={{
        fontSize: 10,
        letterSpacing: 'var(--tracking-mono-caps)',
        textTransform: 'uppercase',
        padding: '3px 10px',
        borderRadius: 'var(--radius-pill)',
        border: '1px solid',
        color: isSoon ? 'var(--text-3)' : 'var(--accent)',
        borderColor: isSoon ? 'var(--border-strong)' : 'var(--accent-bg)',
        background: isSoon ? 'transparent' : 'var(--accent-bg)',
      }}
    >
      {isSoon ? 'soon' : 'shipping'}
    </span>
  );
}

/* ── Mocks ─────────────────────────────────────────────────────────── */

/**
 * Terminal mock. Idle: shows a prompt and one command. On hover: the
 * command "runs" — output lines stream in from below the cursor.
 */
function TerminalMock({ active }: { active: boolean }) {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        background: 'var(--bg-code)',
        borderRadius: 8,
        padding: '10px 12px',
        fontFamily: 'var(--font-mono)',
        fontSize: 11,
        color: '#D8D2C8',
        lineHeight: 1.5,
        overflow: 'hidden',
        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.06)',
        position: 'relative',
      }}
    >
      <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
        <Dot color="#FF5F57" />
        <Dot color="#FEBC2E" />
        <Dot color="#28C840" />
      </div>

      <div>
        <span style={{ color: 'var(--accent-soft)' }}>$</span>{' '}
        <span>slobozip compress</span>{' '}
        <span style={{ color: '#9A9288' }}>-o build.slbz src/</span>
      </div>

      <motion.div
        initial={false}
        animate={{ opacity: active ? 1 : 0, y: active ? 0 : 4 }}
        transition={{ duration: DUR.base, ease: EASE_OUT_QUINT }}
        style={{ marginTop: 4 }}
      >
        <div style={{ color: '#9A9288' }}>[scan] 12 files, 218.4 KiB</div>
        <div style={{ color: '#9A9288' }}>
          [lzss] <span style={{ color: 'var(--accent-soft)' }}>████████████████</span> 100%
        </div>
        <div>
          <span style={{ color: '#5FC7C2' }}>✓</span> 218.4 KiB →{' '}
          <span style={{ color: 'var(--accent-soft)' }}>92.1 KiB</span>{' '}
          <span style={{ color: '#9A9288' }}>(58% saved)</span>
        </div>
      </motion.div>

      <div style={{ marginTop: 4 }}>
        <span style={{ color: 'var(--accent-soft)' }}>$</span>{' '}
        <span
          style={{
            display: 'inline-block',
            width: 6,
            height: 12,
            background: '#D8D2C8',
            verticalAlign: 'middle',
            animation: 'cursorBlink 1s steps(2) infinite',
          }}
        />
      </div>

      <style>{`
        @keyframes cursorBlink { 50% { opacity: 0; } }
      `}</style>
    </div>
  );
}

/**
 * Windows desktop window. Idle: window with a static progress bar at 0.
 * On hover: bar fills in 800ms with the percent number ticking up.
 */
function WindowMock({ active }: { active: boolean }) {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        background: 'var(--bg-card)',
        borderRadius: 8,
        boxShadow: '0 8px 30px rgba(0,0,0,0.06), inset 0 0 0 1px var(--border)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Title bar */}
      <div
        style={{
          padding: '8px 10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg-warm)',
        }}
      >
        <span
          className="t-mono"
          style={{
            fontSize: 10,
            color: 'var(--text-3)',
            letterSpacing: 0.4,
          }}
        >
          SloboZip · desktop
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          <WinControl />
          <WinControl />
          <WinControl />
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div
          style={{
            fontSize: 11,
            color: 'var(--text-2)',
            lineHeight: 1.4,
          }}
        >
          Compressing <strong style={{ color: 'var(--text)' }}>build.slbz</strong>
        </div>

        <div
          style={{
            position: 'relative',
            height: 6,
            background: 'var(--bg-warm)',
            borderRadius: 999,
            overflow: 'hidden',
            border: '1px solid var(--border)',
          }}
        >
          <motion.div
            initial={false}
            animate={{ width: active ? '72%' : '12%' }}
            transition={{ duration: DUR.slow, ease: EASE_OUT_QUINT }}
            style={{
              height: '100%',
              background: 'var(--accent)',
              borderRadius: 999,
            }}
          />
        </div>

        <div
          className="t-mono"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 10,
            color: 'var(--text-3)',
            letterSpacing: 0.4,
          }}
        >
          <Counter target={active ? 72 : 12} suffix="%" />
          <span>3 of 12 files</span>
        </div>
      </div>
    </div>
  );
}

function WinControl() {
  return (
    <span
      style={{
        width: 10,
        height: 10,
        borderRadius: 2,
        background: 'var(--text-4)',
        opacity: 0.6,
      }}
    />
  );
}

/**
 * Browser tab. Idle: SloboZip tab in a generic browser chrome.
 * On hover: a tiny ratio bar shrinks inside the page area.
 */
function BrowserMock({ active }: { active: boolean }) {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        background: 'var(--bg-card)',
        borderRadius: 8,
        boxShadow: '0 8px 30px rgba(0,0,0,0.06), inset 0 0 0 1px var(--border)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Tab bar */}
      <div
        style={{
          height: 26,
          display: 'flex',
          alignItems: 'flex-end',
          padding: '0 8px',
          background: 'var(--bg-warm)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <div
          style={{
            padding: '4px 10px',
            background: 'var(--bg-card)',
            borderRadius: '6px 6px 0 0',
            fontSize: 10,
            color: 'var(--text)',
            border: '1px solid var(--border)',
            borderBottom: 'none',
            fontFamily: 'var(--font-mono)',
            letterSpacing: 0.2,
          }}
        >
          slobozip
        </div>
      </div>

      {/* URL bar */}
      <div
        style={{
          padding: '6px 10px',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <div
          className="t-mono"
          style={{
            background: 'var(--bg-warm)',
            border: '1px solid var(--border)',
            borderRadius: 999,
            padding: '3px 10px',
            fontSize: 10,
            color: 'var(--text-3)',
            letterSpacing: 0.2,
          }}
        >
          slobozip.dev/#squeeze
        </div>
      </div>

      {/* Page */}
      <div style={{ flex: 1, padding: '14px 12px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <div
          className="t-mono"
          style={{
            fontSize: 9,
            color: 'var(--text-3)',
            letterSpacing: 'var(--tracking-mono-caps)',
            textTransform: 'uppercase',
            marginBottom: 6,
          }}
        >
          ratio
        </div>
        <div
          style={{
            position: 'relative',
            height: 4,
            background: 'var(--bg-warm)',
            borderRadius: 999,
            border: '1px solid var(--border)',
            overflow: 'hidden',
          }}
        >
          <motion.div
            initial={false}
            animate={{ width: active ? '38%' : '100%' }}
            transition={{ duration: DUR.slow, ease: EASE_OUT_QUINT }}
            style={{
              height: '100%',
              background: 'var(--accent)',
              borderRadius: 999,
            }}
          />
        </div>
        <div
          className="t-mono"
          style={{
            marginTop: 8,
            fontSize: 10,
            color: 'var(--text-2)',
          }}
        >
          input.txt → <span style={{ color: 'var(--accent)' }}>−62%</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Phone. Idle: device chassis with a quiet screen.
 * On hover: a coral wave drifts down the screen — a small "alive" cue
 * for the surface that hasn't shipped yet.
 */
function PhoneMock({ active }: { active: boolean }) {
  return (
    <div
      style={{
        width: 90,
        height: 156,
        background: 'var(--bg-code)',
        borderRadius: 18,
        padding: 5,
        boxShadow: '0 12px 30px rgba(0,0,0,0.12)',
        position: 'relative',
      }}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          background: 'var(--bg-card)',
          borderRadius: 14,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Notch */}
        <div
          style={{
            position: 'absolute',
            top: 6,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 28,
            height: 6,
            background: 'var(--bg-code)',
            borderRadius: 999,
          }}
        />

        {/* Status bar */}
        <div
          style={{
            paddingTop: 18,
            display: 'flex',
            justifyContent: 'space-between',
            paddingInline: 10,
            fontSize: 7,
            fontFamily: 'var(--font-mono)',
            color: 'var(--text-3)',
            letterSpacing: 0.3,
          }}
        >
          <span>9:41</span>
          <span>●●●</span>
        </div>

        {/* Center wave */}
        <div
          style={{
            position: 'absolute',
            top: 50,
            left: 0,
            right: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: '0 14px',
          }}
        >
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              initial={false}
              animate={{
                opacity: active ? [0.3, 1, 0.3] : 0.18,
                scaleX: active ? [0.7, 1, 0.7] : 0.5,
              }}
              transition={{
                duration: 1.2,
                ease: EASE_IN_OUT_CUBIC,
                repeat: active ? Infinity : 0,
                delay: i * 0.12,
              }}
              style={{
                height: 3,
                background: 'var(--accent)',
                borderRadius: 999,
                transformOrigin: 'left',
              }}
            />
          ))}
        </div>

        {/* Footer */}
        <div
          className="t-mono"
          style={{
            position: 'absolute',
            bottom: 12,
            left: 0,
            right: 0,
            textAlign: 'center',
            fontSize: 7,
            color: 'var(--text-3)',
            letterSpacing: 0.4,
            textTransform: 'uppercase',
          }}
        >
          .slbz
        </div>
      </div>
    </div>
  );
}

function Dot({ color }: { color: string }) {
  return (
    <span
      style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: color,
        opacity: 0.85,
      }}
    />
  );
}

/* ── helpers ───────────────────────────────────────────────────────── */

/**
 * Returns true when the primary input is a coarse pointer (touch).
 * SSR-safe — defaults to false until the effect runs, so server output
 * matches the desktop default. Subscribes to media-query changes so
 * the hook updates if a user switches between input methods (e.g. a
 * tablet docked to a mouse).
 */
function useIsCoarsePointer(): boolean {
  const [coarse, setCoarse] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(pointer: coarse)');
    setCoarse(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setCoarse(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return coarse;
}

/**
 * Lightweight number ticker that eases from its current value to the
 * given target whenever the target prop changes. Used for the
 * Windows-mock progress percent so it counts up on hover.
 */
function Counter({ target, suffix, ms = 700 }: { target: number; suffix?: string; ms?: number }) {
  const [v, setV] = useState(0);

  useEffect(() => {
    const t0 = performance.now();
    const start = v;
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - t0) / ms);
      const eased = 1 - Math.pow(1 - p, 4);
      setV(Math.round(start + (target - start) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // We intentionally don't depend on `v` (would re-fire) or `ms` (constant).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  return (
    <span>
      {v}
      {suffix}
    </span>
  );
}
